/**
 * Editing a set that has already been logged.
 *
 * Deliberately Dexie-free — local types, and nothing imported but the unit
 * conversions — so every rule here is reachable from a test.
 *
 * The rule that shapes the whole file: **only write what changed.** Not as an
 * optimisation. lib/units opens by warning about a weight that drifts every time
 * it is edited, because kg→lb rounds to one decimal and lb→kg to three, and an
 * edit form is precisely the round trip that triggers it. Measured: 100 kg
 * prefills as 220.5 lb, and storing that back gives 100.017 kg. It converges
 * rather than compounding, but a no-op Save that silently moves the stored
 * weight is still a bug — `updateSet` clears the e1rm on any weight change, so
 * the server recomputes from 100.017 and the number on the progress chart moves
 * because you opened a set and closed it again.
 *
 * Comparing the draft against the prefill it started from makes an untouched
 * field genuinely untouched. It also keeps the patch narrow, which protects the
 * fields this editor has no inputs for: `rpe` and `is_failure` can only arrive
 * by voice, and a patch that named them would erase them.
 */

import {
  displayToKg,
  displayToMetres,
  durationToInput,
  kgToDisplay,
  metresToDisplay,
  parseDuration,
  type DistanceUnit,
  type Unit,
} from './units.ts'   // explicit extension: the test loads this module in bare Node

export type Measure = 'weight' | 'reps' | 'time' | 'distance'

/**
 * Only the fields this editor reads or writes.
 *
 * The measures are optional as well as nullable, matching SetRow: a locally
 * created row simply omits the ones its exercise doesn't take, rather than
 * carrying four nulls. `undefined` and `null` mean the same thing here — no
 * value — and both prefill as an empty box.
 */
export type EditableSet = {
  weight_kg?: number | null
  reps?: number | null
  duration_seconds?: number | null
  distance_m?: number | null
  is_warmup: boolean
  notes?: string | null
}

/** The form's state: strings, because that is what an input holds. */
export type SetDraft = {
  weight: string
  reps: string
  duration: string
  distance: string
  note: string
  warmup: boolean
}

/** Prefill the form from a stored set, in the user's units.
 *
 *  `du` must be the same unit the log form used. Read back in miles what was
 *  typed in yards and the editor prefills 0.02 for a 40 yd sprint, which then
 *  reads as a change and rewrites the row on save. */
export function draftFromSet(
  set: EditableSet,
  unit: Unit,
  du: DistanceUnit = 'km',
): SetDraft {
  return {
    weight: str(kgToDisplay(set.weight_kg, unit)),
    reps: str(set.reps),
    duration: durationToInput(set.duration_seconds),
    distance: str(metresToDisplay(set.distance_m, du)),
    note: set.notes ?? '',
    warmup: set.is_warmup,
  }
}

/**
 * The narrowest patch that expresses the edit.
 *
 * Empty when nothing changed, which the caller uses to disable Save — pressing
 * a button that writes nothing should not be possible.
 */
export function patchFromDraft(opts: {
  original: EditableSet
  draft: SetDraft
  unit: Unit
  measures: Measure[]
  du?: DistanceUnit
}): Partial<EditableSet> {
  const { original, draft, unit, measures, du = 'km' } = opts
  const base = draftFromSet(original, unit, du)
  const patch: Partial<EditableSet> = {}

  // Each measure only if the exercise takes it AND the text actually differs.
  // The first half stops a plank acquiring a weight from an input that was on
  // screen for a different exercise; the second stops the rounding drift.
  if (measures.includes('weight') && draft.weight !== base.weight) {
    patch.weight_kg = displayToKg(parseFloat(draft.weight), unit)
  }
  if (measures.includes('reps') && draft.reps !== base.reps) {
    patch.reps = parseInt(draft.reps, 10) || null
  }
  if (measures.includes('time') && draft.duration !== base.duration) {
    patch.duration_seconds = parseDuration(draft.duration)
  }
  if (measures.includes('distance') && draft.distance !== base.distance) {
    patch.distance_m = displayToMetres(parseFloat(draft.distance), du)
  }
  if (draft.note !== base.note) {
    patch.notes = draft.note.trim() || null
  }
  if (draft.warmup !== base.warmup) {
    patch.is_warmup = draft.warmup
  }
  return patch
}

/**
 * Whether the draft still has the measure that makes the set mean anything —
 * reps for a squat, seconds for a plank, metres for a row.
 *
 * The same rule the log form applies before it will create a set. Saving an
 * edit that empties it would leave a row that renders as nothing, which is
 * worse than the set you were trying to correct.
 */
export function draftIsComplete(draft: SetDraft, measures: Measure[]): boolean {
  if (measures.includes('reps')) return (parseInt(draft.reps, 10) || 0) > 0
  if (measures.includes('distance')) return parseFloat(draft.distance) > 0
  if (measures.includes('time')) return (parseDuration(draft.duration) ?? 0) > 0
  return parseFloat(draft.weight) > 0
}

function str(v: number | null | undefined): string {
  return v === null || v === undefined ? '' : String(v)
}
